import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { Loader2, CalendarX, Send, Eye, EyeOff, Check, Pencil } from 'lucide-react';

interface Session {
  id: string;
  session_date: string;
  session_time: string;
  label: string;
  location: string;
  capacity: number;
  check_in_code: string;
  ppt_url: string | null;
  status: 'active' | 'cancelled' | 'closed';
  hidden: number;
  attended_count: number;
  verified_enrollment_count: number;
}

export default function SessionsPanel() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingReminder, setSendingReminder] = useState<{ id: string; type: 't2' } | null>(null);
  const [reminderNotice, setReminderNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingCapacity, setEditingCapacity] = useState<{ id: string; value: string } | null>(null);
  const [savingCapacity, setSavingCapacity] = useState<string | null>(null);
  const [togglingHidden, setTogglingHidden] = useState<string | null>(null);

  async function loadSessions() {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/goodstore/sessions-summary');
      const data = await res.json();
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (e) {
      console.error('Failed to load sessions', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSessions();
  }, []);

  async function handleGenerate() {
    setGenerating(true);
    try {
      await apiFetch('/api/public/admin/goodstore/sessions/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weeksAhead: 2 }),
      });
      await loadSessions();
    } finally {
      setGenerating(false);
    }
  }

  async function handleCancel(id: string) {
    if (!window.confirm('確定要取消這場簡介會嗎？')) return;
    try {
      await apiFetch(`/api/public/admin/goodstore/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      await loadSessions();
    } catch (e) {
      console.error('Failed to cancel session', e);
    }
  }

  async function handleSendReminder(id: string, type: 't2') {
    setSendingReminder({ id, type });
    setReminderNotice(null);
    try {
      const res = await apiFetch(`/api/public/admin/goodstore/sessions/${id}/send-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: type }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setReminderNotice({
          type: 'success',
          text: `已成功發送 ${type.toUpperCase()} 提醒（${data.sent || 0} 人）`,
        });
      } else {
        setReminderNotice({ type: 'error', text: data.error || '發送失敗' });
      }
    } catch (e) {
      setReminderNotice({ type: 'error', text: '發送失敗' });
    } finally {
      setSendingReminder(null);
      setTimeout(() => setReminderNotice(null), 5000);
    }
  }

  function startEditingCapacity(s: Session) {
    setEditingCapacity({ id: s.id, value: String(s.capacity) });
  }

  async function saveCapacity(id: string) {
    if (!editingCapacity || editingCapacity.id !== id) return;
    const capacity = Number(editingCapacity.value);
    if (!Number.isFinite(capacity) || capacity < 0 || capacity > 999) {
      setReminderNotice({ type: 'error', text: '名額必須為 0–999 的數字' });
      return;
    }
    setSavingCapacity(id);
    try {
      const res = await apiFetch(`/api/public/admin/goodstore/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capacity }),
      });
      if (res.ok) {
        setEditingCapacity(null);
        await loadSessions();
      } else {
        const data = await res.json().catch(() => ({}));
        setReminderNotice({ type: 'error', text: data.error || '儲存失敗' });
      }
    } catch (e) {
      console.error('Failed to update capacity', e);
      setReminderNotice({ type: 'error', text: '儲存失敗' });
    } finally {
      setSavingCapacity(null);
      setTimeout(() => setReminderNotice(null), 5000);
    }
  }

  async function toggleHidden(id: string, currentHidden: number) {
    setTogglingHidden(id);
    try {
      const res = await apiFetch(`/api/public/admin/goodstore/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: currentHidden ? 0 : 1 }),
      });
      if (res.ok) {
        await loadSessions();
      } else {
        const data = await res.json().catch(() => ({}));
        setReminderNotice({ type: 'error', text: data.error || '更新失敗' });
      }
    } catch (e) {
      console.error('Failed to toggle hidden', e);
      setReminderNotice({ type: 'error', text: '更新失敗' });
    } finally {
      setTogglingHidden(null);
      setTimeout(() => setReminderNotice(null), 5000);
    }
  }

  const statusLabel: Record<string, string> = {
    active: '進行中',
    cancelled: '已取消',
    closed: '已關閉',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">簡介會場次</h2>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition disabled:opacity-50"
        >
          {generating && <Loader2 size={16} className="animate-spin" />}
          生成未來 2 週場次
        </button>
      </div>

      {reminderNotice && (
        <div className={`p-3 rounded-lg text-sm font-bold ${reminderNotice.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {reminderNotice.text}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-500">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />
          載入中...
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
          暫無場次，請按上方按鈕生成。
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">日期／時間</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">地點</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">名額</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">已報名（已驗證）</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">已出席</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">狀態</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">公開顯示</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-500 dark:text-gray-400">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sessions.map((s) => (
                  <tr key={s.id} className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${s.hidden ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 text-gray-800 dark:text-white font-medium">{s.label}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.location}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {editingCapacity?.id === s.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={999}
                            value={editingCapacity.value}
                            onChange={(e) => setEditingCapacity({ id: s.id, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveCapacity(s.id);
                              if (e.key === 'Escape') setEditingCapacity(null);
                            }}
                            className="w-20 px-2 py-1 text-sm border rounded dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                            autoFocus
                          />
                          <button
                            onClick={() => saveCapacity(s.id)}
                            disabled={savingCapacity === s.id}
                            className="p-1 text-green-700 hover:bg-green-50 rounded disabled:opacity-50"
                          >
                            {savingCapacity === s.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditingCapacity(s)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="點擊修改名額"
                        >
                          {s.capacity}
                          <Pencil size={12} className="text-gray-400" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.verified_enrollment_count}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.attended_count}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                          s.status === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {statusLabel[s.status] || s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleHidden(s.id, s.hidden)}
                        disabled={togglingHidden === s.id}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold transition disabled:opacity-50 ${
                          s.hidden
                            ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}
                        title={s.hidden ? '已隱藏，不會顯示在公開報名頁' : '公開顯示中'}
                      >
                        {togglingHidden === s.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : s.hidden ? (
                          <>
                            <EyeOff size={12} /> 隱藏
                          </>
                        ) : (
                          <>
                            <Eye size={12} /> 顯示
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSendReminder(s.id, 't2')}
                          disabled={sendingReminder?.id === s.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 transition disabled:opacity-50"
                        >
                          {sendingReminder?.id === s.id && sendingReminder?.type === 't2' ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Send size={12} />
                          )}
                          T-2
                        </button>
                        {s.status === 'active' && (
                          <button
                            onClick={() => handleCancel(s.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition"
                          >
                            <CalendarX size={12} />
                            取消
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
